import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Close,
  ContentCopy,
  DeleteOutlined,
  OpenInNew,
  Refresh,
  Save,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { AppHeader } from '../components/app-header';
import { BackButton } from '../components/back-button';
import { LoadingState } from '../components/loading-state';
import {
  useGetSystemNotificationsConfigQuery,
  useUpdateSystemNotificationsConfigMutation,
} from '../store/hooks';
import {
  buildNotification,
  cleanForSave,
  formatStatsigTimestamp,
  fromDateTimeLocalValue,
  getMutationStatus,
  nextUniqueId,
  normalizeNotifications,
  SEVERITIES,
  slugify,
  TARGETING_SCOPES,
  toDateTimeLocalValue,
  TYPES,
  validateNotifications,
} from './system-notifications-utils';
import type {
  EditableNotification,
  NotificationSeverity,
  NotificationType,
  TargetingScope,
} from './system-notifications-utils';
import type { SystemNotificationRuleWarning } from '@store/generated/admin';

export default function SystemNotificationsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error, refetch, isFetching } = useGetSystemNotificationsConfigQuery();
  const [updateConfig, { isLoading: isSaving }] = useUpdateSystemNotificationsConfigMutation();
  const [notifications, setNotifications] = useState<EditableNotification[]>([]);
  const [baselineNotifications, setBaselineNotifications] = useState<EditableNotification[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!data) return;
    const loadedNotifications = normalizeNotifications(data.notifications ?? []);
    setNotifications(loadedNotifications);
    setBaselineNotifications(loadedNotifications);
    setSelectedIndex(loadedNotifications.length ? 0 : -1);
  }, [data]);

  const validation = useMemo(() => validateNotifications(notifications), [notifications]);
  const selectedNotification = selectedIndex >= 0 ? notifications[selectedIndex] : undefined;
  const selectedErrors = selectedIndex >= 0 ? (validation.errorsByIndex[selectedIndex] ?? []) : [];
  const dirty = useMemo(
    () =>
      JSON.stringify(normalizeNotifications(notifications)) !==
      JSON.stringify(baselineNotifications),
    [baselineNotifications, notifications],
  );
  const canSave = Boolean(data) && dirty && validation.isValid && !isSaving;

  const handleAdd = useCallback(() => {
    setNotifications(current => {
      const existingIds = new Set(current.map(notification => notification.id));
      const next = buildNotification(existingIds);
      setSelectedIndex(current.length);
      return [...current, next];
    });
  }, []);

  const handleDuplicate = useCallback(() => {
    if (!selectedNotification) return;
    setNotifications(current => {
      const existingIds = new Set(current.map(notification => notification.id));
      const duplicate = {
        ...selectedNotification,
        id: nextUniqueId(`${selectedNotification.id}-copy`, existingIds),
        title: `${selectedNotification.title || 'Notification'} copy`,
        link: selectedNotification.link ? { ...selectedNotification.link } : undefined,
        targeting: {
          ...selectedNotification.targeting,
          routes: selectedNotification.targeting.routes
            ? [...selectedNotification.targeting.routes]
            : undefined,
        },
      };
      setSelectedIndex(selectedIndex + 1);
      return [
        ...current.slice(0, selectedIndex + 1),
        duplicate,
        ...current.slice(selectedIndex + 1),
      ];
    });
  }, [selectedIndex, selectedNotification]);

  const handleDelete = useCallback(() => {
    if (!selectedNotification) return;
    setNotifications(current => {
      const next = current.filter((_, index) => index !== selectedIndex);
      setSelectedIndex(next.length ? Math.min(selectedIndex, next.length - 1) : -1);
      return next;
    });
  }, [selectedIndex, selectedNotification]);

  const handleMove = useCallback(
    (direction: -1 | 1) => {
      const targetIndex = selectedIndex + direction;
      if (targetIndex < 0 || targetIndex >= notifications.length) return;

      setNotifications(current => {
        const next = [...current];
        const selected = next[selectedIndex];
        next[selectedIndex] = next[targetIndex];
        next[targetIndex] = selected;
        return next;
      });
      setSelectedIndex(targetIndex);
    },
    [notifications.length, selectedIndex],
  );

  const handleSelectedChange = useCallback(
    (patch: Partial<EditableNotification>) => {
      setNotifications(current =>
        current.map((notification, index) =>
          index === selectedIndex ? { ...notification, ...patch } : notification,
        ),
      );
    },
    [selectedIndex],
  );

  const handleTargetingChange = useCallback(
    (patch: Partial<EditableNotification['targeting']>) => {
      if (!selectedNotification) return;
      handleSelectedChange({
        targeting: {
          ...selectedNotification.targeting,
          ...patch,
        },
      });
    },
    [handleSelectedChange, selectedNotification],
  );

  const handleReload = useCallback(async () => {
    await refetch();
    enqueueSnackbar('System notifications reloaded', { variant: 'info' });
  }, [enqueueSnackbar, refetch]);

  const handleSave = useCallback(async () => {
    if (!data || !validation.isValid) return;
    setConfirmOpen(false);

    try {
      await updateConfig({
        updateSystemNotificationsConfigRequest: {
          expected_last_modified_time: data.last_modified_time,
          notifications: notifications.map(cleanForSave),
        },
      }).unwrap();
      const refreshed = await refetch();
      const latestNotifications = normalizeNotifications(
        refreshed.data?.notifications ?? notifications,
      );
      setNotifications(latestNotifications);
      setBaselineNotifications(latestNotifications);
      setSelectedIndex(
        latestNotifications.length ? Math.min(selectedIndex, latestNotifications.length - 1) : -1,
      );
      enqueueSnackbar('System notifications saved', { variant: 'success' });
    } catch (saveError) {
      const status = getMutationStatus(saveError);
      if (status === 409) {
        enqueueSnackbar('Config changed in Statsig; reload before saving.', { variant: 'error' });
        return;
      }
      enqueueSnackbar('Failed to save system notifications.', { variant: 'error' });
    }
  }, [
    data,
    enqueueSnackbar,
    notifications,
    refetch,
    selectedIndex,
    updateConfig,
    validation.isValid,
  ]);

  if (isLoading && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <LoadingState message="Loading system notifications..." />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-4">
            <BackButton fallbackUrl="/admin-v2" />
          </div>
          <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
            <AlertTitle>Unable to load system notifications</AlertTitle>
            The Statsig configuration could not be fetched.
          </Alert>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-4">
          <BackButton fallbackUrl="/admin-v2" />
        </div>

        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Notifications</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600">
              <Chip
                label={data?.is_enabled ? 'Statsig enabled' : 'Statsig disabled'}
                color={data?.is_enabled ? 'success' : 'warning'}
                size="small"
                variant="outlined"
              />
              <span>Last modified {formatStatsigTimestamp(data?.last_modified_time)}</span>
              {data?.last_modifier_name && <span>by {data.last_modifier_name}</span>}
              {data?.permalink && (
                <Link
                  href={data.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1"
                >
                  Statsig Console <OpenInNew sx={{ fontSize: 14 }} />
                </Link>
              )}
            </div>
          </div>

          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => void handleReload()}
              disabled={isFetching || isSaving}
            >
              Reload
            </Button>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={() => setConfirmOpen(true)}
              disabled={!canSave}
            >
              Save
            </Button>
          </Stack>
        </div>

        <RuleWarnings warnings={data?.rule_warnings ?? []} />

        {!validation.isValid && (
          <Alert severity="error" className="mb-4">
            <AlertTitle>Fix validation errors before saving</AlertTitle>
            <ul className="m-0 pl-5">
              {Array.from(new Set(validation.errors)).map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[360px_1fr]">
          <Card>
            <CardHeader
              title="Notifications"
              action={
                <Tooltip title="Add notification">
                  <IconButton onClick={handleAdd} aria-label="add notification">
                    <Add />
                  </IconButton>
                </Tooltip>
              }
            />
            <Divider />
            <CardContent className="p-0">
              {notifications.length === 0 ? (
                <div className="p-6 text-sm text-gray-500">No notifications configured.</div>
              ) : (
                <Stack spacing={0.5} className="p-2">
                  {notifications.map((notification, index) => {
                    const active = index === selectedIndex;
                    const hasError = Boolean(validation.errorsByIndex[index]?.length);
                    return (
                      <button
                        type="button"
                        key={`${notification.id}-${index}`}
                        onClick={() => setSelectedIndex(index)}
                        className={`w-full rounded-md border px-3 py-3 text-left transition ${
                          active
                            ? 'border-blue-500 bg-blue-50'
                            : hasError
                              ? 'border-red-200 bg-red-50 hover:bg-red-100'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {notification.title || notification.id || 'Untitled notification'}
                            </div>
                            <div className="mt-1 truncate font-mono text-xs text-gray-500">
                              {notification.id || 'missing-id'}
                            </div>
                          </div>
                          <Stack direction="row" spacing={0.5}>
                            <Chip label={notification.type} size="small" variant="outlined" />
                            <Chip
                              label={notification.severity}
                              size="small"
                              color={notification.severity}
                              variant="soft"
                            />
                          </Stack>
                        </div>
                      </button>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>

          <Stack spacing={3}>
            <Card>
              <CardHeader
                title={selectedNotification ? 'Notification Editor' : 'Notification Editor'}
                action={
                  selectedNotification && (
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            onClick={() => handleMove(-1)}
                            disabled={selectedIndex <= 0}
                            aria-label="move notification up"
                          >
                            <ArrowUpward />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            onClick={() => handleMove(1)}
                            disabled={
                              selectedIndex < 0 || selectedIndex >= notifications.length - 1
                            }
                            aria-label="move notification down"
                          >
                            <ArrowDownward />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton onClick={handleDuplicate} aria-label="duplicate notification">
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={handleDelete}
                          aria-label="delete notification"
                          color="error"
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )
                }
              />
              <Divider />
              <CardContent>
                {selectedNotification ? (
                  <NotificationForm
                    notification={selectedNotification}
                    errors={selectedErrors}
                    onChange={handleSelectedChange}
                    onTargetingChange={handleTargetingChange}
                  />
                ) : (
                  <div className="py-12 text-center text-sm text-gray-500">
                    Select or add a notification.
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedNotification && (
              <Card>
                <CardHeader title="Preview" />
                <Divider />
                <CardContent>
                  <NotificationPreview notification={selectedNotification} />
                </CardContent>
              </Card>
            )}
          </Stack>
        </div>
      </main>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Save System Notifications</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will update the Statsig default value for system_notifications.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => void handleSave()} variant="contained" disabled={!canSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

interface RuleWarningsProps {
  warnings: SystemNotificationRuleWarning[];
}

function RuleWarnings({ warnings }: RuleWarningsProps) {
  if (!warnings.length) return null;

  return (
    <Alert severity="warning" className="mb-4">
      <AlertTitle>Statsig rules may override defaults</AlertTitle>
      <Stack spacing={1}>
        {warnings.map(warning => (
          <div key={warning.rule_id} className="text-sm">
            <span className="font-medium">{warning.rule_name}</span>
            <span className="text-gray-700"> ({warning.pass_percentage}%): </span>
            <span>{warning.message}</span>
          </div>
        ))}
      </Stack>
    </Alert>
  );
}

interface NotificationFormProps {
  notification: EditableNotification;
  errors: string[];
  onChange: (patch: Partial<EditableNotification>) => void;
  onTargetingChange: (patch: Partial<EditableNotification['targeting']>) => void;
}

function NotificationForm({
  notification,
  errors,
  onChange,
  onTargetingChange,
}: NotificationFormProps) {
  const hasLink = Boolean(notification.link);

  return (
    <Stack spacing={3}>
      {errors.length > 0 && (
        <Alert severity="error">
          <ul className="m-0 pl-5">
            {errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <TextField
          label="ID"
          value={notification.id}
          onChange={event => onChange({ id: slugify(event.target.value) || event.target.value })}
          fullWidth
          size="small"
        />
        <TextField
          select
          label="Type"
          value={notification.type}
          onChange={event => onChange({ type: event.target.value as NotificationType })}
          fullWidth
          size="small"
        >
          {TYPES.map(type => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Severity"
          value={notification.severity}
          onChange={event => onChange({ severity: event.target.value as NotificationSeverity })}
          fullWidth
          size="small"
        >
          {SEVERITIES.map(severity => (
            <MenuItem key={severity} value={severity}>
              {severity}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <TextField
        label="Title"
        value={notification.title}
        onChange={event => onChange({ title: event.target.value })}
        fullWidth
        size="small"
      />
      <TextField
        label="Message"
        value={notification.message}
        onChange={event => onChange({ message: event.target.value })}
        fullWidth
        multiline
        minRows={3}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControlLabel
          control={
            <Switch
              checked={notification.dismissible}
              onChange={event => onChange({ dismissible: event.target.checked })}
            />
          }
          label="Dismissible"
        />
        <FormControlLabel
          control={
            <Switch
              checked={notification.persistent}
              onChange={event => onChange({ persistent: event.target.checked })}
            />
          }
          label="Remember dismissal"
        />
      </Stack>

      <Divider />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <TextField
          select
          label="Targeting"
          value={notification.targeting.scope}
          onChange={event => {
            const scope = event.target.value as TargetingScope;
            onTargetingChange({
              scope,
              routes:
                scope === 'route' ? (notification.targeting.routes ?? ['/admin-v2']) : undefined,
            });
          }}
          fullWidth
          size="small"
        >
          {TARGETING_SCOPES.map(scope => (
            <MenuItem key={scope} value={scope}>
              {scope}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Starts"
          type="datetime-local"
          value={toDateTimeLocalValue(notification.startsAt)}
          onChange={event => onChange({ startsAt: fromDateTimeLocalValue(event.target.value) })}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          label="Expires"
          type="datetime-local"
          value={toDateTimeLocalValue(notification.expiresAt)}
          onChange={event => onChange({ expiresAt: fromDateTimeLocalValue(event.target.value) })}
          size="small"
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </div>

      {notification.targeting.scope === 'route' && (
        <TextField
          label="Routes"
          value={(notification.targeting.routes ?? []).join('\n')}
          onChange={event =>
            onTargetingChange({
              routes: event.target.value
                .split(/\n|,/)
                .map(route => route.trim())
                .filter(Boolean),
            })
          }
          fullWidth
          multiline
          minRows={3}
        />
      )}

      <Divider />

      <FormControlLabel
        control={
          <Switch
            checked={hasLink}
            onChange={event =>
              onChange({
                link: event.target.checked ? { url: '', text: '' } : undefined,
              })
            }
          />
        }
        label="CTA link"
      />

      {notification.link && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <TextField
            label="Link text"
            value={notification.link.text}
            onChange={event =>
              onChange({
                link: {
                  ...notification.link!,
                  text: event.target.value,
                },
              })
            }
            fullWidth
            size="small"
          />
          <TextField
            label="Link URL"
            value={notification.link.url}
            onChange={event =>
              onChange({
                link: {
                  ...notification.link!,
                  url: event.target.value,
                },
              })
            }
            fullWidth
            size="small"
          />
        </div>
      )}
    </Stack>
  );
}

interface NotificationPreviewProps {
  notification: EditableNotification;
}

function NotificationPreview({ notification }: NotificationPreviewProps) {
  if (notification.type === 'banner') {
    return (
      <Alert
        severity={notification.severity}
        action={
          notification.dismissible && (
            <IconButton aria-label="dismiss notification preview" size="small" color="inherit">
              <Close fontSize="inherit" />
            </IconButton>
          )
        }
        sx={{ borderRadius: 0 }}
      >
        <Box>
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            {notification.title || 'Notification title'}
          </Typography>
          <Typography variant="body2" component="span" sx={{ ml: 1 }}>
            {notification.message || 'Notification message'}
          </Typography>
          {notification.link?.url && notification.link.text && (
            <Link
              href={notification.link.url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ml: 1 }}
            >
              {notification.link.text}
            </Link>
          )}
        </Box>
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ maxWidth: 520 }}>
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" component="span" color={notification.severity}>
          {notification.title || 'Notification title'}
        </Typography>
        {notification.dismissible && (
          <IconButton aria-label="close notification preview" size="small">
            <Close />
          </IconButton>
        )}
      </Box>
      <Divider />
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body1" component="p">
          {notification.message || 'Notification message'}
        </Typography>
        {notification.link?.url && notification.link.text && (
          <Box sx={{ mt: 2 }}>
            <Link href={notification.link.url} target="_blank" rel="noopener noreferrer">
              {notification.link.text}
            </Link>
          </Box>
        )}
      </Box>
      <Divider />
      <Box sx={{ px: 3, py: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" color={notification.severity}>
          {notification.dismissible ? 'Dismiss' : 'Close'}
        </Button>
      </Box>
    </Paper>
  );
}
